/**
 * @file seo.js
 * @description 客户端 SEO：document head meta / Open Graph / JSON-LD
 * @module shared/utils/seo
 *
 * 可选在根目录 .env.production 中设置 VUE_APP_SITE_ORIGIN=https://你的域名（无末尾斜杠），用于相对资源绝对化。
 *
 * Bing / Google 站长工具（部署后由运维操作）：
 * 1. 打开 https://www.bing.com/webmasters 验证 www.jzmanga.com
 * 2. 提交站点地图 https://www.jzmanga.com/sitemap.xml（与 robots.txt 中声明一致）
 * 3. 用 URL 检查确认首页、favicon、/img/brand-logo-512.png 可抓取
 * 4. 搜索结果旁图标由引擎决定，通常需数天到数周才可能更新
 */

/** History 为真实路径 URL；若仍用 Hash 路由则含 #。用于 canonical / og:url */
function getCurrentPageUrlForSeo() {
  if (typeof window === 'undefined') return ''
  const { origin, pathname, search, hash } = window.location
  return `${origin}${pathname}${search}${hash}`
}

export const SITE_BRAND = '橘子漫嘎'

export const DEFAULT_SITE_DESCRIPTION =
  '橘子漫嘎提供在线漫画阅读，支持下拉式阅读、卷目录更新与评论互动，发现你想看的故事。'

/** 品牌正方形 Logo（JSON-LD / 默认 og:image），置于 public/img */
export const DEFAULT_BRAND_OG_IMAGE = '/img/brand-logo-512.png'

const SITE_JSON_LD_SCRIPT_ID = 'jzcomic-site-jsonld'

export function truncateText(str, maxLen = 160) {
  if (!str || typeof str !== 'string') return ''
  const t = str.replace(/\s+/g, ' ').trim()
  if (t.length <= maxLen) return t
  return `${t.slice(0, maxLen - 1)}…`
}

export function getPreferredOrigin() {
  if (typeof window === 'undefined') return ''
  const env = process.env.VUE_APP_SITE_ORIGIN
  if (env && String(env).trim()) return String(env).trim().replace(/\/$/, '')
  return window.location.origin
}

export function toAbsoluteUrl(url) {
  if (!url || typeof url !== 'string') return ''
  const u = url.trim()
  if (/^https?:\/\//i.test(u)) return u
  if (typeof window === 'undefined') return u
  if (u.startsWith('//')) return `${window.location.protocol}${u}`
  const origin = getPreferredOrigin()
  if (u.startsWith('/')) return `${origin}${u}`
  return `${origin}/${u.replace(/^\//, '')}`
}

function setMetaName(name, content) {
  if (content === undefined || content === null) return
  let el = document.querySelector(`meta[name="${name}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('name', name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', String(content))
}

function setMetaProperty(property, content) {
  if (content === undefined || content === null) return
  let el = document.querySelector(`meta[property="${property}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('property', property)
    document.head.appendChild(el)
  }
  el.setAttribute('content', String(content))
}

function setCanonical(href) {
  if (!href) return
  let el = document.querySelector('link[rel="canonical"]')
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'canonical')
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

/**
 * @param {object} opts
 * @param {string} opts.title - 完整 document.title
 * @param {string} [opts.description]
 * @param {string} [opts.ogImage] - 封面等；缺省时使用品牌默认图
 * @param {string} [opts.ogUrl] - 默认当前页 href
 * @param {string} [opts.ogType] - website | article
 */
export function applyPageSeo({
  title,
  description,
  ogImage,
  ogUrl,
  ogType = 'website'
}) {
  if (typeof document === 'undefined') return

  if (title) document.title = title

  const desc = truncateText(description || DEFAULT_SITE_DESCRIPTION, 160)
  setMetaName('description', desc)

  setMetaProperty('og:title', title || SITE_BRAND)
  setMetaProperty('og:description', truncateText(description || DEFAULT_SITE_DESCRIPTION, 200))
  setMetaProperty('og:type', ogType)

  const pageUrl = ogUrl || (typeof window !== 'undefined' ? getCurrentPageUrlForSeo() : '')
  if (pageUrl) {
    setMetaProperty('og:url', pageUrl)
    setCanonical(pageUrl)
  }

  const imagePath = ogImage || DEFAULT_BRAND_OG_IMAGE
  const abs = toAbsoluteUrl(imagePath)
  setMetaProperty('og:image', abs)
  setMetaName('twitter:card', 'summary_large_image')
  setMetaName('twitter:image', abs)
}

/**
 * 注入站点级 Organization + WebSite JSON-LD（幂等，按固定 script id 更新）
 */
export function applySiteJsonLd() {
  if (typeof document === 'undefined') return

  const origin = getPreferredOrigin()
  if (!origin) return

  const siteUrl = `${origin}/`
  const logoUrl = toAbsoluteUrl(DEFAULT_BRAND_OG_IMAGE)
  const orgId = `${origin}/#organization`
  const websiteId = `${origin}/#website`

  const payload = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': orgId,
        name: SITE_BRAND,
        url: siteUrl,
        logo: {
          '@type': 'ImageObject',
          url: logoUrl,
        },
      },
      {
        '@type': 'WebSite',
        '@id': websiteId,
        name: SITE_BRAND,
        url: siteUrl,
        publisher: { '@id': orgId },
        inLanguage: 'zh-CN',
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${origin}/comicsearch`,
          },
          'query-input': 'required name=search_term_string',
        },
      },
    ],
  }

  let el = document.getElementById(SITE_JSON_LD_SCRIPT_ID)
  if (!el) {
    el = document.createElement('script')
    el.id = SITE_JSON_LD_SCRIPT_ID
    el.type = 'application/ld+json'
    document.head.appendChild(el)
  }
  el.textContent = JSON.stringify(payload)
}

export function applyComicDetailSeo(comic) {
  if (!comic || typeof comic !== 'object') return
  const name = comic.name || '漫画'
  const rawDesc =
    comic.synop && String(comic.synop).trim()
      ? comic.synop
      : `《${name}》在线漫画，共 ${comic.volTotal || 0} 卷，尽在${SITE_BRAND}。`
  const title = `${name} - 漫画详情 - ${SITE_BRAND}`
  const pageUrl = getCurrentPageUrlForSeo()
  applyPageSeo({
    title,
    description: rawDesc,
    ogImage: comic.cover,
    ogUrl: pageUrl || undefined,
    ogType: 'article'
  })
}

export function applyComicReaderSeo(comicInfoVo, volNum) {
  if (!comicInfoVo || typeof comicInfoVo !== 'object') return
  const name = comicInfoVo.name || '漫画'
  const v = Number(volNum) || 1
  const rawDesc =
    comicInfoVo.synop && String(comicInfoVo.synop).trim()
      ? `${comicInfoVo.synop}（第${v}卷）`
      : `正在阅读《${name}》第 ${v} 卷 - ${SITE_BRAND}`
  const title = `《${name}》第${v}卷 - ${SITE_BRAND}`
  const pageUrl = getCurrentPageUrlForSeo()
  applyPageSeo({
    title,
    description: rawDesc,
    ogImage: comicInfoVo.cover,
    ogUrl: pageUrl || undefined,
    ogType: 'article'
  })
}
